import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, throwError } from 'rxjs';

// HTTP options for application/x-www-form-urlencoded (matching AngularJS $http behavior)
const HTTP_OPTIONS = {
  headers: new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
  }),
  withCredentials: true
};

// ============================================
// INTERFACES
// ============================================

export interface AccountInfo {
  P_SYS_ACCT_KEY?: string;
  P_SYS_ACCT_DESC?: string;
  P_LOCAL_ACCT_KEY?: string;
  P_LOCAL_ACCT_DESC?: string;
}

export interface ScheduleMInfo {
  SCHDM_TEMPLATE_ID?: string;
  SCHDM_DESC?: string;
  M_TEMPLATE_KEY?: string;
}

// ============================================
// SERVICE
// ============================================

@Injectable({ 
    providedIn: 'root'
 })
export class ExpenseTransAccountDetailsService {

  private urls = {
    accountInfo: '/loadJsonObject?action_code=30cny0',
    scheduleMInfo: '/loadJsonObject?action_code=lrs0ke'
  };

  // Global params from AngularJS
  private globalParams: any = {};
  // Filter params from AngularJS workspace filters
  private filterParams: any = {};

  constructor(private http: HttpClient) {}

  // ============================================
  // SET GLOBAL/FILTER PARAMS (called from component)
  // ============================================
  
  setGlobalParams(globalParams: any): void {
    this.globalParams = globalParams || {};
  }

  setFilterParams(filterParams: any): void {
    this.filterParams = filterParams || {};
  }

  // ============================================
  // API METHODS
  // ============================================

  /**
   * Get Account Information (System Account + Local Account)
   * Action Code: 30cny0
   * Used when MAP_DESC == 'by Account'
   */
  getAccountInfo(baseUrl: string, transCatgKey: number): Observable<AccountInfo[]> {
    const url = `${baseUrl}${this.urls.accountInfo}`;
    const body = this.buildFormData({
      trans_catg_key: transCatgKey.toString()
    });
    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject || [];
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching account info:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get Schedule M Information
   * Action Code: lrs0ke
   * Used when MAP_DESC == 'by Schedule M'
   */
  getScheduleMInfo(baseUrl: string, transCatgKey: number): Observable<ScheduleMInfo[]> {
    const url = `${baseUrl}${this.urls.scheduleMInfo}`;
    const body = this.buildFormData({
      trans_catg_key: transCatgKey.toString()
    });
    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject || [];
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching schedule M info:', error);
        return throwError(() => error);
      })
    );
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  /**
   * Build FormData with merged params (like AngularJS JsonObjectFactory.getJSONObj)
   */
  private buildFormData(params: { [key: string]: string }): HttpParams {
    const mergedParams: { [key: string]: string } = {};
    
    // First add globalParams
    if (this.globalParams) {
      Object.keys(this.globalParams).forEach(key => {
        const value = this.globalParams[key];
        if (value !== undefined && value !== null && value !== '') {
          mergedParams[key] = String(value);
        }
      });
    }
    
    // Then add filterParams
    if (this.filterParams) {
      Object.keys(this.filterParams).forEach(key => {
        const value = this.filterParams[key];
        if (value !== undefined && value !== null && value !== '') {
          mergedParams[key] = String(value);
        }
      });
    }
    
    // Finally add specific params
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        mergedParams[key] = params[key];
      }
    });
    
    // Build HttpParams
    let httpParams = new HttpParams();
    Object.keys(mergedParams).forEach(key => {
      httpParams = httpParams.set(key, mergedParams[key]);
    });
    
    return httpParams;
  }
}
